import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DisplaySetting {
  id: string;
  field_name: string;
  field_label: string;
  show_in_search: boolean;
  show_in_detail: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function useDisplaySettings() {
  return useQuery({
    queryKey: ['display-settings'],
    queryFn: async (): Promise<DisplaySetting[]> => {
      const { data, error } = await supabase
        .from('display_settings')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 30,
  });
}

export function useSearchDisplaySettings() {
  return useQuery({
    queryKey: ['display-settings', 'search'],
    queryFn: async (): Promise<DisplaySetting[]> => {
      const { data, error } = await supabase
        .from('display_settings')
        .select('*')
        .eq('show_in_search', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

export function useDetailDisplaySettings() {
  return useQuery({
    queryKey: ['display-settings', 'detail'],
    queryFn: async (): Promise<DisplaySetting[]> => {
      const { data, error } = await supabase
        .from('display_settings')
        .select('*')
        .eq('show_in_detail', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

export function useUpdateDisplaySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<DisplaySetting> & { id: string }) => {
      const { id, ...updates } = settings;
      const { data, error } = await supabase
        .from('display_settings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['display-settings'] });
    },
  });
}

export function useUpsertDisplaySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Omit<DisplaySetting, 'id' | 'created_at' | 'updated_at'>[]) => {
      const { data, error } = await supabase
        .from('display_settings')
        .upsert(settings, { onConflict: 'field_name' })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['display-settings'] });
    },
  });
}

// Helper function to check if a value should be displayed
export function shouldDisplayValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '' || normalized === 'n/a' || normalized === '-' || normalized === 'na') return false;
  }
  if (typeof value === 'number' && isNaN(value)) return false;
  return true;
}

// Map field names to their corresponding data sources
export const fieldDataMapping: Record<string, { source: 'variant' | 'specs' | 'price' | 'model'; key: string }> = {
  price_dkk: { source: 'price', key: 'price_dkk' },
  powertrain_type: { source: 'variant', key: 'powertrain_type' },
  range_wltp_km: { source: 'specs', key: 'range_wltp_km' },
  power_hp: { source: 'specs', key: 'power_hp' },
  acceleration_0_100: { source: 'specs', key: 'acceleration_0_100' },
  top_speed_kmh: { source: 'specs', key: 'top_speed_kmh' },
  battery_kwh: { source: 'specs', key: 'battery_kwh' },
  consumption_wh_km: { source: 'specs', key: 'consumption_wh_km' },
  l_per_100km: { source: 'specs', key: 'l_per_100km' },
  charging_dc_kw: { source: 'specs', key: 'charging_dc_kw' },
  charging_ac_kw: { source: 'specs', key: 'charging_ac_kw' },
  dc_charge_10_80_min: { source: 'specs', key: 'dc_charge_10_80_min' },
  co2_g_km: { source: 'specs', key: 'co2_g_km' },
  seats: { source: 'variant', key: 'seats' },
  trunk_l: { source: 'variant', key: 'trunk_l' },
  tow_kg_braked: { source: 'variant', key: 'tow_kg_braked' },
  drivetrain: { source: 'variant', key: 'drivetrain' },
  transmission: { source: 'variant', key: 'transmission' },
  curb_weight_kg: { source: 'variant', key: 'curb_weight_kg' },
  doors: { source: 'variant', key: 'doors' },
  warranty_years: { source: 'variant', key: 'warranty_years' },
  body_type: { source: 'model', key: 'body_type' },
  owner_tax_yearly_dkk: { source: 'variant', key: 'owner_tax_yearly_dkk' },
  green_owner_tax_yearly_dkk: { source: 'variant', key: 'green_owner_tax_yearly_dkk' },
  // New fields
  trim_level: { source: 'variant', key: 'trim_level' },
  tow_bar_standard: { source: 'variant', key: 'tow_bar_standard' },
  torque_nm: { source: 'specs', key: 'torque_nm' },
  min_wheel_size_inches: { source: 'variant', key: 'min_wheel_size_inches' },
  euro_ncap: { source: 'variant', key: 'euro_ncap' },
  heat_pump_standard: { source: 'variant', key: 'heat_pump_standard' },
  // Dimensioner
  length_m: { source: 'variant', key: 'length_m' },
  width_m: { source: 'variant', key: 'width_m' },
  height_m: { source: 'variant', key: 'height_m' },
  wheelbase_m: { source: 'variant', key: 'wheelbase_m' },
  turning_diameter_m: { source: 'variant', key: 'turning_diameter_m' },
  // Garanti
  warranty_km: { source: 'variant', key: 'warranty_km' },
  battery_warranty_years: { source: 'variant', key: 'battery_warranty_years' },
  battery_warranty_km: { source: 'variant', key: 'battery_warranty_km' },
  // Andre
  is_commercial: { source: 'variant', key: 'is_commercial' },
  fuel_tank_l: { source: 'specs', key: 'fuel_tank_l' },
};
